import { useId, useState } from 'react';
import { usePermissions } from '@/features/auth';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  LoadingSpinner,
  QueryErrorAlert,
  ToggleGroup,
} from '@/shared/components';
import { useTemplateVersionQuery } from '../../hooks/useTemplateVersionQuery';
import { useVersionPreviewQuery } from '../../hooks/useVersionPreviewQuery';
import type { TemplateVersionSummary } from '../../types/documentTemplates.types';
import { actorLabel, formatDay, STATUS_BADGE_VARIANTS, STATUS_LABELS } from '../../utils/versionLabels';
import { PreviewFrame } from '../PreviewFrame';

type ViewMode = 'document' | 'html';

interface Props {
  templateId: string;
  version: TemplateVersionSummary;
  hasDraft: boolean;
  canRestore: boolean;
  isRestoring: boolean;
  onRestore: (versionId: string) => void;
  onOpenDraft?: () => void;
}

function versionDates(version: TemplateVersionSummary): string {
  const created = `created ${formatDay(version.createdAt)} by ${actorLabel(version.createdByName)}`;
  return version.publishedAt
    ? `Published ${formatDay(version.publishedAt)} by ${actorLabel(version.publishedByName)} · ${created}`
    : created.charAt(0).toUpperCase() + created.slice(1);
}

function SourceView({ templateId, versionId }: { templateId: string; versionId: string }) {
  const query = useTemplateVersionQuery(templateId, versionId);
  if (query.isLoading) return <LoadingSpinner size="lg" className="py-12" message="Loading version..." />;
  if (query.isError || !query.data) {
    return (
      <QueryErrorAlert message="Could not load this version." onRetry={() => query.refetch()} isRetrying={query.isFetching} />
    );
  }
  return (
    <pre
      tabIndex={0}
      aria-label="Template body source"
      className="max-h-[32rem] overflow-auto whitespace-pre-wrap rounded-lg border border-mp-border bg-mp-surface p-4 font-mono text-sm"
    >
      {query.data.bodyHtml}
    </pre>
  );
}

function DocumentView({ templateId, version }: { templateId: string; version: TemplateVersionSummary }) {
  const query = useVersionPreviewQuery(templateId, version.id, version.revision, true);
  return <PreviewFrame isLoading={query.isLoading} error={query.error} preview={query.data ?? null} className="h-[32rem]" />;
}

function DetailActions({ version, hasDraft, canRestore, isRestoring, onRestore, onOpenDraft }: Omit<Props, 'templateId'>) {
  const hintId = useId();

  if (version.status === 'draft') {
    return (
      <>
        <span className="mr-auto text-sm text-mp-muted">Not published yet.</span>
        {onOpenDraft && (
          <Button type="button" size="sm" variant="neutral" appearance="outline" onClick={onOpenDraft}>
            Open draft
          </Button>
        )}
      </>
    );
  }
  if (!canRestore) return null;
  return (
    <>
      {hasDraft && (
        <span id={hintId} className="mr-auto text-sm text-mp-muted">
          A draft already exists. Publish or discard it before restoring.
        </span>
      )}
      <Button
        type="button"
        size="sm"
        variant="neutral" appearance="outline"
        disabled={isRestoring}
        ariaDisabled={hasDraft}
        ariaDescribedBy={hasDraft ? hintId : undefined}
        onClick={() => onRestore(version.id)}
      >
        {isRestoring ? 'Restoring...' : 'Restore as draft'}
      </Button>
    </>
  );
}

export function VersionDetail({ templateId, version, canRestore, ...actions }: Props) {
  const [mode, setMode] = useState<ViewMode>('document');
  const headingId = useId();
  const { hasPermission } = usePermissions();
  const canRestoreHere = canRestore && hasPermission({ resource: 'document_template', action: 'edit' });
  const hasActions = version.status === 'draft' || canRestoreHere;

  return (
    <Card ariaLabelledBy={headingId}>
      <CardHeader className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <CardTitle id={headingId}>
              Version {version.versionNumber}
            </CardTitle>
            <Badge variant={STATUS_BADGE_VARIANTS[version.status]}>{STATUS_LABELS[version.status]}</Badge>
          </div>
          <p className="mt-1 text-sm text-mp-muted">{versionDates(version)}</p>
        </div>
        <ToggleGroup<ViewMode>
          ariaLabel="View"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'document', label: 'Document' },
            { value: 'html', label: 'HTML' },
          ]}
        />
      </CardHeader>
      <CardContent className="space-y-4 py-5">
        {version.status !== 'draft' && (
          <div className="rounded-lg bg-mp-background px-4 py-3 text-sm">
            <p className="text-xs text-mp-muted">Change note</p>
            <p className={version.changeNote ? undefined : 'text-mp-muted'}>{version.changeNote || 'No change note'}</p>
          </div>
        )}
        <div className="rounded-lg bg-mp-background p-4">
          {mode === 'document' ? (
            <DocumentView templateId={templateId} version={version} />
          ) : (
            <SourceView templateId={templateId} versionId={version.id} />
          )}
        </div>
      </CardContent>
      {hasActions && (
        <CardFooter className="flex-wrap">
          <DetailActions version={version} canRestore={canRestoreHere} {...actions} />
        </CardFooter>
      )}
    </Card>
  );
}
